// frontend/src/features/tasks/TaskDefinitionForm.tsx

import { useState, useMemo, useEffect } from 'react';
import {
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Grid,
  Fieldset,
  Alert,
  Text,
  Switch,
  Paper,
} from '@mantine/core';
import { DateTimePicker, DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconCalendarEvent, IconTrash } from '@tabler/icons-react';
import { useTaskTypes, useTaskCompletionsByDefinition } from '@/hooks/useTasks';
import { TaskHistory } from './TaskHistory';
import { useEquipmentList } from '@/hooks/useEquipment';
import { RecurrenceSelector } from './RecurrenceSelector';
import { ChemicalSelect } from '@/components/ChemicalSelect';
import { ParameterSelect } from '@/components/ParameterSelect';
import {
  type TaskDefinition,
  type TaskDefinitionCreate,
  type TaskType,
  type Recurrence,
} from '@/types/task';
import { useAuth } from '@/contexts/AuthContext';

interface TaskDefinitionFormProps {
  definition?: TaskDefinition;
  onSubmit: (data: TaskDefinitionCreate, desiredStatus?: 'active' | 'paused') => void;
  onCancel: () => void;
  isLoading?: boolean;
  onDelete?: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface FormValues {
  name: string;
  task_type: TaskType | '';
  is_recurring: boolean; // Toggle for recurring vs one-time
  recurrence: Recurrence;
  equipment_id: string;
  // One-time task fields
  due_date: Date | null;
  notes: string;
  // Template data fields
  instructions: string;
  chemical: string;
  amount: number | '';
  brand: string;
  parameter: string;
  volume: number | '';
  percentage: number | '';
}

// Task types that can link to equipment
const EQUIPMENT_LINKABLE_TYPES: TaskType[] = [
  'cleaning',
  'calibration',
  'replacement',
  'media_change',
  'inspection',
];

export function TaskDefinitionForm({
  definition,
  onSubmit,
  onCancel,
  isLoading = false,
  onDelete,
  onDirtyChange,
}: TaskDefinitionFormProps) {
  const { user } = useAuth();
  const volumeLabel = user?.volume_unit === 'gallons' ? 'gallons' : 'litres';
  const { data: taskTypesData } = useTaskTypes();
  const { data: equipmentData } = useEquipmentList();
  const isEditMode = !!definition;

  // Local state for the schedule active/paused toggle
  const [scheduleActive, setScheduleActive] = useState(definition?.status === 'active');

  // Fetch completions for this task definition (only in edit mode)
  const { data: completionsData, isLoading: completionsLoading } = useTaskCompletionsByDefinition(
    definition?.id
  );

  const defaultRecurrence: Recurrence = {
    interval: 1,
    unit: 'week',
    days_of_week: [],
  };

  const form = useForm<FormValues>({
    initialValues: {
      name: definition?.name ?? '',
      task_type: definition?.task_type ?? '',
      is_recurring: !!definition?.recurrence,
      recurrence: definition?.recurrence ?? defaultRecurrence,
      equipment_id: definition?.equipment_id ?? '',
      // One-time task fields
      due_date: null,
      notes: '',
      // Template data
      instructions: definition?.template_data?.instructions ?? '',
      chemical: definition?.template_data?.chemical ?? '',
      amount: definition?.template_data?.amount ?? '',
      brand: definition?.template_data?.brand ?? '',
      parameter: definition?.template_data?.parameter ?? '',
      volume: definition?.template_data?.volume ?? '',
      percentage: definition?.template_data?.percentage ?? '',
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Name is required'),
      task_type: (value) => (value ? null : 'Task type is required'),
    },
  });

  // Track dirty state (form fields + schedule toggle)
  const statusChanged = isEditMode && definition
    ? scheduleActive !== (definition.status === 'active')
    : false;
  const isFormDirty = form.isDirty();

  useEffect(() => {
    onDirtyChange?.(isFormDirty || statusChanged);
  }, [isFormDirty, statusChanged, onDirtyChange]);

  // Build task type options
  const taskTypeOptions = useMemo(() => {
    if (!taskTypesData?.types) return [];
    return taskTypesData.types.map((type) => ({
      value: type,
      label: taskTypesData.labels[type] || type,
    }));
  }, [taskTypesData]);

  // Build equipment options
  const equipmentOptions = useMemo(() => {
    if (!equipmentData?.equipment) return [];
    return equipmentData.equipment.map((eq) => ({
      value: eq.id,
      label: eq.name,
    }));
  }, [equipmentData]);

  // Determine which template fields to show based on task_type
  const currentTaskType = form.values.task_type as TaskType;
  const showDosingFields = currentTaskType === 'dosing';
  const showTestingFields = currentTaskType === 'testing';
  const showWaterChangeFields = currentTaskType === 'water_change';
  const showEquipmentSelect = currentTaskType && EQUIPMENT_LINKABLE_TYPES.includes(currentTaskType);

  // Determine which schedule fields to show
  const showRecurrenceFields = form.values.is_recurring;
  const showOneTimeFields = !form.values.is_recurring;

  const handleSubmit = (values: FormValues) => {
    const data: TaskDefinitionCreate = {
      name: values.name.trim(),
      task_type: values.task_type as TaskType,
    };

    if (values.is_recurring) {
      data.recurrence = values.recurrence;
      if (values.due_date) {
        data.due_date = values.due_date.toISOString();
      }
    } else {
      // One-time task: explicitly clear recurrence so updates persist
      data.recurrence = null;
      if (values.due_date) {
        data.due_date = values.due_date.toISOString();
      }
      if (values.notes) {
        data.notes = values.notes;
      }
    }

    // Add equipment_id if selected
    if (values.equipment_id) {
      data.equipment_id = values.equipment_id;
    }

    // Build template_data based on task type
    const templateData: Record<string, unknown> = {};

    if (values.instructions) {
      templateData.instructions = values.instructions;
    }

    if (showDosingFields) {
      if (values.chemical) templateData.chemical = values.chemical;
      if (values.amount) templateData.amount = Number(values.amount);
      if (values.brand) templateData.brand = values.brand;
    }

    if (showTestingFields && values.parameter) {
      templateData.parameter = values.parameter;
    }

    if (showWaterChangeFields) {
      if (values.volume) templateData.volume = Number(values.volume);
      if (values.percentage) templateData.percentage = Number(values.percentage);
    }

    if (Object.keys(templateData).length > 0) {
      data.template_data = templateData;
    }

    const desiredStatus = statusChanged
      ? (scheduleActive ? 'active' : 'paused') as 'active' | 'paused'
      : undefined;
    onSubmit(data, desiredStatus);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Fieldset legend="Basic Information">
          <Stack gap="sm">
            <TextInput
              label="Name"
              placeholder="e.g., Clean protein skimmer"
              required
              {...form.getInputProps('name')}
            />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Task Type"
                  placeholder="Select task type"
                  required
                  data={taskTypeOptions}
                  {...form.getInputProps('task_type')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Schedule"
                  data={[
                    { value: 'recurring', label: 'Recurring' },
                    { value: 'one-time', label: 'One-time' },
                  ]}
                  value={form.values.is_recurring ? 'recurring' : 'one-time'}
                  onChange={(v) => form.setFieldValue('is_recurring', v === 'recurring')}
                />
              </Grid.Col>
            </Grid>

            {/* Pause/Resume toggle for recurring tasks in edit mode */}
            {isEditMode && definition && form.values.is_recurring && (
              <Paper withBorder p="sm" radius="md" bg="gray.0">
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text size="sm" fw={600}>Active Schedule</Text>
                    <Text size="xs" c="dimmed">
                      {scheduleActive
                        ? 'New instances are created automatically'
                        : 'Scheduling paused — no new instances'}
                    </Text>
                  </div>
                  <Switch
                    checked={scheduleActive}
                    onChange={() => setScheduleActive((prev) => !prev)}
                    size="md"
                    color="green"
                    aria-label="Toggle scheduling"
                  />
                </Group>
              </Paper>
            )}

            {showRecurrenceFields && (
              <>
                <RecurrenceSelector
                  value={form.values.recurrence}
                  onChange={(r) => form.setFieldValue('recurrence', r)}
                  taskName={form.values.name}
                />
                <DateInput
                  label="Start Date"
                  placeholder="Leave empty to start now"
                  clearable
                  minDate={new Date()}
                  popoverProps={{ withinPortal: true }}
                  value={form.values.due_date}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue('due_date', new Date(val));
                    } else {
                      form.setFieldValue('due_date', null);
                    }
                  }}
                />
                {/* Next due date display (edit mode only) */}
                {isEditMode && definition?.next_due_at && (
                  <Alert
                    variant="light"
                    color="blue"
                    icon={<IconCalendarEvent size={16} />}
                    radius="md"
                    data-testid="next-due-date"
                  >
                    <Text size="sm">
                      <Text span fw={500}>Next scheduled: </Text>
                      {new Date(definition.next_due_at).toLocaleDateString(undefined, {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        ...(new Date(definition.next_due_at).getFullYear() !== new Date().getFullYear()
                          ? { year: 'numeric' }
                          : {}),
                      })}
                    </Text>
                  </Alert>
                )}
              </>
            )}

            {showOneTimeFields && (
              <>
                <DateTimePicker
                  label="Due Date"
                  placeholder="Leave empty for due now"
                  clearable
                  minDate={new Date()}
                  popoverProps={{ withinPortal: true }}
                  value={form.values.due_date}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue('due_date', new Date(val));
                    } else {
                      form.setFieldValue('due_date', null);
                    }
                  }}
                />
                <Textarea
                  label="Notes"
                  placeholder="Optional notes for this task..."
                  minRows={2}
                  {...form.getInputProps('notes')}
                />
              </>
            )}

            {showEquipmentSelect && (
              <Select
                label="Equipment"
                placeholder="Select equipment (optional)"
                data={equipmentOptions}
                clearable
                {...form.getInputProps('equipment_id')}
              />
            )}
          </Stack>
        </Fieldset>

        {/* Template data fields based on task type */}
        {form.values.task_type && (
          <Fieldset legend="Task Details">
            <Stack gap="sm">
              {currentTaskType && (
                <Textarea
                  label="Instructions"
                  placeholder="Detailed instructions for this task..."
                  minRows={3}
                  {...form.getInputProps('instructions')}
                />
              )}

              {showDosingFields && (
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <ChemicalSelect
                        value={form.values.chemical || ''}
                        onChange={(val) => form.setFieldValue('chemical', val)}
                        placeholder="e.g., Calcium"
                        error={form.errors.chemical as string}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Brand"
                        placeholder="e.g., Red Sea"
                        {...form.getInputProps('brand')}
                      />
                    </Grid.Col>
                  </Grid>
                  <NumberInput
                    label="Amount"
                    placeholder="Amount in ml"
                    min={0}
                    decimalScale={2}
                    {...form.getInputProps('amount')}
                  />
                </>
              )}

              {showTestingFields && (
                <ParameterSelect
                  value={form.values.parameter}
                  onChange={(val) => form.setFieldValue('parameter', val)}
                  error={form.errors.parameter as string}
                />
              )}

              {showWaterChangeFields && (
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label={`Volume (${volumeLabel})`}
                      placeholder={`Volume in ${volumeLabel}`}
                      min={0}
                      decimalScale={1}
                      {...form.getInputProps('volume')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label="Percentage"
                      placeholder="Percentage of tank"
                      min={0}
                      max={100}
                      {...form.getInputProps('percentage')}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
          </Fieldset>
        )}

        <Group justify={isEditMode && onDelete ? 'space-between' : 'flex-end'} mt="md">
          {isEditMode && definition && onDelete && (
            <Button
              color="red"
              variant="subtle"
              leftSection={<IconTrash size={16} />}
              onClick={() => onDelete(definition.id)}
              aria-label="Delete task"
            >
              Delete
            </Button>
          )}
          <Group>
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} disabled={isLoading}>
              {isEditMode ? 'Update Task' : 'Create Task'}
            </Button>
          </Group>
        </Group>

        {/* Task History - only show in edit mode */}
        {isEditMode && definition && (
          <TaskHistory
            createdAt={definition.created_at}
            completions={completionsData?.completions ?? []}
            isLoading={completionsLoading}
          />
        )}
      </Stack>
    </form>
  );
}
